import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
    IconButton,
    Menu,
    MenuGroup,
    MenuTrigger,
    MenuContent,
    MenuCheckboxItem,
    MenuSeparator,
} from '@cad-arc';
import { useAdditionalInfos } from '../../hooks/use-additional-infos/additional-infos-provider';
import {
    useFilterConfig,
    FilterConfig,
} from '../../hooks/use-additional-infos/use-additional-infos-filter';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { AdditionalInfoType } from '../../types';
import styles from './additional-infos-filter.module.css';

/** One participating class option — mirrors CrossClassInformation.crossClassOptions. */
export type CrossClassFilterOption = { label: string; value: number };

type AdditionalInfosFilterProps = {
    isFirstResponder?: boolean;
    /** Participating agency-type classes on this event (Police/Fire/EMS). The
     *  Cross-Class Message Type section renders only when 2+ are present. */
    crossClassOptions?: CrossClassFilterOption[];
};

export const AdditionalInfosFilter = observer(
    ({ isFirstResponder, crossClassOptions }: AdditionalInfosFilterProps) => {
        const rawFilterConfig = useFilterConfig();
        const { rollOutCadNarrativeFilteringAndFocusImprovementsEnabled } = useEnabledFeatures();

        const activeFilterConfig = useMemo(() => {
            return Object.entries(rawFilterConfig).reduce(
                (acc, [key, config]) => {
                    const isEnabled = config.checkEnabled ?? true;

                    if (isEnabled) {
                        acc[key] = config;
                    }

                    return acc;
                },
                {} as Record<string, FilterConfig>
            );
        }, [rawFilterConfig]);

        const defaultFilters = useMemo(() => {
            const filters = [AdditionalInfoType.USER];

            Object.values(activeFilterConfig).forEach((config) => {
                if (config.isDefaultEnabled) {
                    filters.push(...config.types);
                }
            });

            if (isFirstResponder && !activeFilterConfig.proqaUpdates) {
                filters.push(AdditionalInfoType.PROQA);
            }

            return filters;
        }, [activeFilterConfig, isFirstResponder]);

        const {
            filter = defaultFilters,
            setFilter,
            agencyTypeGlobalAttrIds,
            toggleAgencyTypeFilter,
            syncAgencyTypeFilter,
        } = useAdditionalInfos();

        // Cross-Class Message Type section shows only for a genuine cross-class
        // event (2+ participating classes) with the rollout flag on. Single-class
        // events (and BOLOs, which have no cross-class options) hide it entirely.
        const showCrossClassSection =
            rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
            (crossClassOptions?.length ?? 0) >= 2;

        useEffect(
            function syncCrossClassFilterForEvent() {
                syncAgencyTypeFilter(
                    showCrossClassSection && crossClassOptions
                        ? crossClassOptions.map((option) => option.value)
                        : []
                );
            },
            [showCrossClassSection, crossClassOptions, syncAgencyTypeFilter]
        );

        const handleFilterChange = useCallback(
            (key: keyof typeof activeFilterConfig, checked: boolean) => {
                const config = activeFilterConfig[key];

                if (!config) {
                    return;
                }

                const newFilters = checked
                    ? Array.from(new Set([...filter, ...config.types]))
                    : filter.filter((f) => !config.types.includes(f));

                setFilter(newFilters);
            },
            [filter, activeFilterConfig, setFilter]
        );

        return (
            <Menu>
                <MenuTrigger asChild>
                    <IconButton
                        aria-label="Filter"
                        icon="Filter"
                        variant="ghost"
                        data-testid="ADDITIONAL_INFOS_FILTER_BUTTON"
                    />
                </MenuTrigger>
                <MenuContent
                    className={isFirstResponder ? styles['first-responder-filter-menu'] : undefined}
                >
                    {showCrossClassSection && crossClassOptions && (
                        <>
                            <MenuGroup title="Cross-Class Message Type">
                                {crossClassOptions.map((option) => (
                                    <MenuCheckboxItem
                                        key={option.value}
                                        isChecked={agencyTypeGlobalAttrIds.has(option.value)}
                                        onSelect={(event) => event.preventDefault()}
                                        onCheckedChange={() => toggleAgencyTypeFilter(option.value)}
                                        data-testid={`ADDITIONAL_INFOS_FILTER_AGENCY_TYPE_${option.value}_CHECKBOX`}
                                    >
                                        {option.label}
                                    </MenuCheckboxItem>
                                ))}
                            </MenuGroup>
                            <MenuSeparator />
                        </>
                    )}
                    <MenuGroup
                        title={
                            rollOutCadNarrativeFilteringAndFocusImprovementsEnabled
                                ? 'Message Source'
                                : 'Narrative Type'
                        }
                    >
                        {Object.entries(activeFilterConfig).map(([key, config]) => {
                            const isChecked = config.types.every((type) => filter.includes(type));

                            return (
                                <MenuCheckboxItem
                                    key={key}
                                    isChecked={isChecked}
                                    onSelect={(event) => event.preventDefault()}
                                    onCheckedChange={(checked) =>
                                        handleFilterChange(
                                            key as keyof typeof activeFilterConfig,
                                            checked
                                        )
                                    }
                                    data-testid={config.testId}
                                >
                                    {config.label}
                                </MenuCheckboxItem>
                            );
                        })}
                    </MenuGroup>
                </MenuContent>
            </Menu>
        );
    }
);
